import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { createHttpErrorMessage } from "@/utils/create-http-error-message";

export const browserTokenConfirmResponseSchema = z.object({
  message: z.string(),
});

export type BrowserTokenConfirmResponse = z.infer<
  typeof browserTokenConfirmResponseSchema
>;

const BROWSER_TOKEN_ENDPOINT = `${NIKE_URL}/browser-token`;

export const updateBrowserToken = async ({
  browserToken,
  backendToken,
}: {
  browserToken: string;
  backendToken: string;
}): Promise<BrowserTokenConfirmResponse> => {
  if (!browserToken) throw new Error("Missing browser token");
  if (!backendToken) throw new Error("Missing backend token");

  const res = await fetch(BROWSER_TOKEN_ENDPOINT, {
    method: "PUT",
    headers: {
      "X-User-Profile": backendToken,
      "X-Browser-Token": browserToken,
    },
  });

  if (!res.ok) {
    if (res.status === 400) {
      throw new Error("Missing browser token header");
    }
    const errorMessage = await createHttpErrorMessage(
      BROWSER_TOKEN_ENDPOINT,
      res
    );
    throw new Error(errorMessage);
  }

  return browserTokenConfirmResponseSchema.parse(await res.json());
};

export const useUpdateBrowserToken = ({
  browserToken,
}: {
  browserToken: string;
}) => {
  const { t } = useLang();
  const queryClient = useQueryClient();
  const { backendToken } = useAuth();

  return useMutation(
    ({ browserToken }: { browserToken: string }) =>
      updateBrowserToken({
        browserToken,
        backendToken: backendToken ?? "",
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["browser-token", browserToken, backendToken],
        });
      },
      onError: () => {
        toast.error(t.auth.multiDevices.updateConnectionFailed);
      },
    }
  );
};
